/**
 * Pure helpers for the Tasks page components.
 *
 * Kept in their own module so unit tests can import the shaping logic
 * without dragging in React, TanStack Query, or the `@/api` path-alias
 * the source components use. Each helper here is referenced by exactly
 * one UI module (the comment notes which one) — extracting them is
 * purely about test ergonomics.
 *
 * Owner modules:
 *   - `categoryDotClass`     → today-tasks-bell.tsx
 *   - `formatRelativeDate`   → task-card.tsx
 *   - `formatLabel`          → sheets/generic-detail.tsx
 *   - `deepLinkForSubject`   → sheets/generic-detail.tsx
 *   - `isDueByEndOfToday`    → notifications/today-tasks-bell.tsx
 *   - `selectDueTodayTasks`  → notifications/today-tasks-bell.tsx
 *   - `computeTaskDisplayPriority` → task-card.tsx
 *   - `formatDaysOverdue`    → notifications/today-tasks-bell.tsx
 *   - `bellTaskSections`     → notifications/today-tasks-bell.tsx
 *   - `workflowRouteForTask`       → tasks.tsx (card click target)
 */

// Avoid the `@/` path alias so bun test can resolve the module from
// apps/ordo's root without a tsconfig-paths plugin (the alias only
// works under Vite + tsc; bun's test runner resolves relative paths
// directly).
import { taskRollupKey } from '../../../../shared/task-rollup-key'
import { type MyTaskRow, type TaskCategory, type TaskShipmentMeta } from '../../api/tasks'

/**
 * The two school-side shipment tasks are the Production Records page's
 * per-day tasks (PROD-1277). Stored rows created before the rename still
 * carry "Confirm delivery received" / "Complete service" as their title, so
 * every surface renders the label from the subKind instead.
 */
export const SCHOOL_TASK_LABELS: Readonly<Record<string, string>> = {
  SCHOOL_CONFIRM_DELIVERY_RECEIVED: 'Transport logs',
  SCHOOL_COMPLETE_SERVICE: 'Service records',
}

/** The `?task=` value the Production Records page opens for a school task kind. */
const PRODUCTION_TASK_PARAM: Readonly<Record<string, 'transport-logs' | 'service-records'>> = {
  SCHOOL_CONFIRM_DELIVERY_RECEIVED: 'transport-logs',
  SCHOOL_COMPLETE_SERVICE: 'service-records',
}

export function isSchoolProductionTask(subKind: string | null | undefined): boolean {
  return subKind != null && subKind in PRODUCTION_TASK_PARAM
}

/** Human label for a subKind: the school task names, else the generic Title Case. */
export function taskKindLabel(subKind: string): string {
  return SCHOOL_TASK_LABELS[subKind] ?? formatLabel(subKind)
}

/** The row title the inbox shows — the subKind label for school tasks, the stored title otherwise. */
export function displayTitleForTask(task: Pick<MyTaskRow, 'subKind' | 'title'>): string {
  return (task.subKind && SCHOOL_TASK_LABELS[task.subKind]) ?? task.title
}

/**
 * Maps a BlockingTaskCategory to a tailwind background class for the
 * leading status dot. PAYMENT_BLOCKING + FINANCIAL wear destructive
 * tone because they hold money flow; OPERATIONS leans amber because
 * ops surfaces drive most operator clicks.
 */
export function categoryDotClass(category: TaskCategory): string {
  switch (category) {
    case 'PAYMENT_BLOCKING':
    case 'FINANCIAL':
      return 'bg-destructive'
    case 'COMPLIANCE':
    case 'LEGAL':
      return 'bg-amber-500'
    case 'OPERATIONS':
      return 'bg-orange-500'
    case 'ONBOARDING':
      return 'bg-blue-500'
    case 'DATA_QUALITY':
      return 'bg-violet-500'
    default:
      return 'bg-muted-foreground'
  }
}

/**
 * Tag badge for a task row. Onboarding tasks get an explicit "Onboarding"
 * badge so a school admin can tell their setup checklist apart from ordinary
 * operator tasks. Returns null for non-onboarding tasks (no badge). `tone`
 * matches `@ordo/ui` TaskListTone values.
 */
export function onboardingTagFor(task: Pick<MyTaskRow, 'category'>): { label: string; tone: 'muted' } | null {
  // `muted` → the subtle secondary badge other apps use for task tags, rather
  // than the loud solid-primary badge.
  return task.category === 'ONBOARDING' ? { label: 'Onboarding', tone: 'muted' } : null
}

/**
 * Human-friendly relative date: today / tomorrow / yesterday / "in N
 * days" / "N days ago", falling back to MMM d. Counts CALENDAR days
 * (matching `computeTaskDisplayPriority`), not rounded 24h windows —
 * a task due at 23:59 tonight reads "today", never "tomorrow". Pure
 * (+ `now`-injectable) so the table can compute it deterministically.
 */
export function formatRelativeDate(iso: string, now: Date = new Date()): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return iso
  }
  // Date-only values (onboarding due dates) are stored as UTC midnight;
  // deriving the calendar day from local Date parts shifts it one back in
  // UTC-negative zones (F-7). The sentinel only matches exact UTC midnight,
  // so real operator-task timestamps keep local-day semantics.
  const dateOnly = calendarDayParts(iso)
  const targetDay = dateOnly
    ? new Date(dateOnly.year, dateOnly.month - 1, dateOnly.day)
    : new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  // Round so a DST hour shift can't skew the whole-day count.
  const days = Math.round((targetDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
  if (days === 0) {
    return 'today'
  }
  if (days === 1) {
    return 'tomorrow'
  }
  if (days === -1) {
    return 'yesterday'
  }
  if (days > 1 && days < 14) {
    return `in ${days} days`
  }
  if (days < -1 && days > -14) {
    return `${Math.abs(days)} days ago`
  }
  return targetDay.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * Parses a date-only value — `YYYY-MM-DD` or its UTC-midnight ISO encoding
 * (`YYYY-MM-DDT00:00:00[.000]Z`) — into calendar-day parts. Returns null for
 * anything else (real timestamps, garbage), so callers can fall back to
 * local-time semantics.
 */
export function calendarDayParts(iso: string): { year: number; month: number; day: number } | null {
  if (!/^\d{4}-\d{2}-\d{2}(T00:00:00(\.\d+)?Z)?$/.test(iso)) {
    return null
  }
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number)
  return { year: year!, month: month!, day: day! }
}

/**
 * Map an `UPPER_SNAKE_CASE` category / subKind to a human label
 * ("Compliance", "Chef Confirm Delivery").
 */
export function formatLabel(raw: string): string {
  return raw
    .toLowerCase()
    .split('_')
    .map((word) => (word.length === 0 ? word : word[0].toUpperCase() + word.slice(1)))
    .join(' ')
}

/**
 * Subset of `KitchenBlockingTaskSummary` the kitchen-strip adapter
 * consumes. Declared here (rather than imported from
 * `@/api/kitchen-orders`) so this module stays import-free of React /
 * TanStack — `bun test` resolves it without the Vite alias.
 */
export interface KitchenBlockingTaskAdapterInput {
  id: string
  category: string
  subKind: string | null
  title: string
  description: string | null
  status: string
  dueAt: string | null
  blocksSubjectKind: string
  blocksSubjectId: string | null
  organizationId: string
}

/**
 * Synthesize a `MyTaskRow` from a `KitchenBlockingTaskSummary` so the
 * shared `TaskDetailDrawer` can render against a kitchen-strip-card
 * click. The strip's payload is a narrower projection — these fields
 * are stubbed because the sheet bodies only read id / subKind / title
 * / description / blocksSubjectKind / blocksSubjectId.
 *
 *   createdAt / organization.name / blocksAction / assignedToProfileCode / taskerTaskId
 *
 * are placeholder values. If a future sheet starts depending on them,
 * we'll need a `GET /api/tasks/:id` endpoint instead of the adapter.
 */
export function kitchenTaskToMyTaskRow(task: KitchenBlockingTaskAdapterInput): MyTaskRow {
  return {
    id: task.id,
    organizationId: task.organizationId,
    category: task.category as TaskCategory,
    subKind: task.subKind,
    title: task.title,
    description: task.description,
    blocksAction: '',
    blocksSubjectKind: task.blocksSubjectKind,
    blocksSubjectId: task.blocksSubjectId,
    status: narrowTaskStatus(task.status),
    dueAt: task.dueAt,
    createdAt: new Date(0).toISOString(),
    assignedToProfileCode: null,
    taskerTaskId: null,
    organization: { id: task.organizationId, name: '' },
  }
}

function narrowTaskStatus(raw: string): MyTaskRow['status'] {
  switch (raw) {
    case 'OPEN':
    case 'IN_PROGRESS':
    case 'COMPLETED':
    case 'DISMISSED':
      return raw
    default:
      return 'OPEN'
  }
}

/**
 * Compute a deep-link URL for a BlockingTask subject. Returns null
 * when the subject kind doesn't have a corresponding apps/ordo page
 * yet — the generic sheet then hides the "Open in full UI" button
 * rather than rendering a broken link.
 */
export function deepLinkForSubject(kind: string, id: string | null): string | null {
  if (!id) {
    return null
  }
  switch (kind) {
    case 'ORDER_BATCH':
      return `/orders?order=${encodeURIComponent(id)}`
    case 'SHIPMENT':
      return `/cafeteria?shipment=${encodeURIComponent(id)}`
    case 'PRODUCTION_RECORD':
      return `/production/${encodeURIComponent(id)}`
    // MENU / MENU_PUBLICATION / BUNDLE / COMPONENT used to deep-link into
    // the universal /recipes catalog browser. The Aug 2026 chef-sidebar
    // split deleted that browser (/recipes is now the kitchen-membership-
    // gated weekly make-list, which cannot resolve a specific subject), so
    // these kinds have no corresponding page again — return null and let
    // the sheet hide the button rather than render a dead link.
    case 'ACCOUNT':
      return `/billing?account=${encodeURIComponent(id)}`
    default:
      return null
  }
}

/**
 * True when `iso` falls on or before the end of `now`'s calendar day —
 * i.e. the task is due today or already overdue. Both are surfaced in
 * the top-bar "due today" bell because both need attention now. Pure so
 * the bell can compute it deterministically + unit tests can pin it.
 */
export function isDueByEndOfToday(iso: string | null, now: Date = new Date()): boolean {
  if (!iso) {
    return false
  }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) {
    return false
  }
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
  return d.getTime() <= endOfToday.getTime()
}

/**
 * Display-priority bucket for a task's due date, driving the status
 * badge on the task card. Pure + `now`-injectable for tests.
 */
export type TaskDisplayPriority = 'overdue' | 'due_today' | 'due_tomorrow' | 'upcoming'

export function computeTaskDisplayPriority(iso: string | null, now: Date = new Date()): TaskDisplayPriority {
  if (!iso) {
    return 'upcoming'
  }
  const target = new Date(iso)
  if (Number.isNaN(target.getTime())) {
    return 'upcoming'
  }
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const targetDay = new Date(target.getFullYear(), target.getMonth(), target.getDate())
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  if (targetDay.getTime() < today.getTime()) {
    return 'overdue'
  }
  if (targetDay.getTime() === today.getTime()) {
    return 'due_today'
  }
  if (targetDay.getTime() === tomorrow.getTime()) {
    return 'due_tomorrow'
  }
  return 'upcoming'
}

/**
 * How late an overdue task is ("1 day overdue", "26 days overdue"), counted in
 * local calendar days exactly like `computeTaskDisplayPriority`, so every task
 * in the Overdue bucket reads at least a day late. Not `formatRelativeDate`:
 * its date-only sentinel also catches a 5pm Pacific daylight due time (exactly
 * UTC midnight) and would label that overdue task "today".
 */
export function formatDaysOverdue(iso: string, now: Date = new Date()): string {
  const due = new Date(iso)
  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate())
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  // Round so a DST hour shift can't skew the whole-day count.
  const days = Math.round((today.getTime() - dueDay.getTime()) / (1000 * 60 * 60 * 24))
  return `${days} day${days === 1 ? '' : 's'} overdue`
}

/** The row fields `workflowRouteForTask` reads. */
export type WorkflowRouteTask = Pick<MyTaskRow, 'id' | 'subKind'> &
  Partial<Pick<MyTaskRow, 'blocksSubjectId'>> & {
    shipment?: Pick<TaskShipmentMeta, 'schoolLocationId' | 'serviceDate'> | null
  }

/**
 * The `/production` deep link that opens a school task's wizard: the campus
 * tab + the SERVICE day (a confirm-delivery task is anchored to its truck's
 * day, but the admin page keys Transport logs by the day the food is served
 * and opens one wizard step per truck). Null without shipment metadata.
 */
export function productionRecordsTaskRoute(task: WorkflowRouteTask): string | null {
  const param = task.subKind ? PRODUCTION_TASK_PARAM[task.subKind] : undefined
  const s = task.shipment
  if (!param || !s) {
    return null
  }
  const params = new URLSearchParams({ locationId: s.schoolLocationId, day: s.serviceDate, task: param })
  return `/production?${params.toString()}`
}

/**
 * The in-app workflow route a task card opens. Operator tasks resolve
 * to the full-page surface where the action is actually taken (e.g.
 * temperature logs, the Production Records wizard). Returns null when the
 * task has no dedicated workflow page — the caller then falls back to the
 * detail drawer.
 */
export function workflowRouteForTask(task: WorkflowRouteTask): string | null {
  if (task.subKind === 'SCHOOL_ONBOARDING') {
    if (task.id.startsWith('chef-documents:')) {
      return `/onboarding/${encodeURIComponent(task.id)}`
    }
    // The inbox carries one aggregate "Complete onboarding" row per org;
    // the wizard itself picks the first incomplete task.
    return '/onboarding'
  }
  if (task.subKind === 'CHEF_SUBMIT_TEMP_LOG') {
    return `/tasks/${task.id}/temperature-log`
  }
  if (isSchoolProductionTask(task.subKind)) {
    // Transport logs / Service records live on the Production Records page
    // (PROD-1277). Without the shipment block (a row projected without it)
    // there is nothing to key the day on — the link-only sheet takes over.
    return productionRecordsTaskRoute(task)
  }
  return null
}

/**
 * Merge the chef's synthesized one-row-per-day temp-log tasks into the
 * real inbox list. The server materializes CHEF_SUBMIT_TEMP_LOG per
 * SHIPMENT (one per location+mealtime+day); rendering those raw rows
 * alongside the day rows would double-count, so the raw rows are
 * dropped and the day rows take their place. Every other real task
 * passes through untouched.
 */
export function mergeChefTempLogDayRows(
  realTasks: ReadonlyArray<MyTaskRow>,
  dayRows: ReadonlyArray<MyTaskRow>,
): MyTaskRow[] {
  return [...realTasks.filter((t) => t.subKind !== 'CHEF_SUBMIT_TEMP_LOG'), ...dayRows]
}

/**
 * Due-date buckets for the Tasks page sections (Overdue / Due Today /
 * Due Later / No Due Date). Pure + `now`-injectable so the page can map
 * them straight onto `@ordo/ui` `TaskList` sections and tests can pin
 * the placement.
 */
export interface DueBuckets {
  overdue: MyTaskRow[]
  dueToday: MyTaskRow[]
  dueLater: MyTaskRow[]
  noDueDate: MyTaskRow[]
}

function dueAtTime(iso: string | null): number {
  if (!iso) {
    return Number.POSITIVE_INFINITY
  }
  const t = new Date(iso).getTime()
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t
}

/**
 * Group tasks into the four Tasks-page sections by due date. Tasks with
 * no due date go to `noDueDate`; everything else is bucketed via
 * `computeTaskDisplayPriority` (due-tomorrow + upcoming both read as
 * "Due Later"). The three dated buckets are sorted soonest-first;
 * `noDueDate` keeps input order.
 */
export function bucketTasksByDue(tasks: ReadonlyArray<MyTaskRow>, now: Date = new Date()): DueBuckets {
  const buckets: DueBuckets = { overdue: [], dueToday: [], dueLater: [], noDueDate: [] }
  for (const task of tasks) {
    if (!task.dueAt) {
      buckets.noDueDate.push(task)
      continue
    }
    switch (computeTaskDisplayPriority(task.dueAt, now)) {
      case 'overdue':
        buckets.overdue.push(task)
        break
      case 'due_today':
        buckets.dueToday.push(task)
        break
      default:
        buckets.dueLater.push(task)
        break
    }
  }
  const bySoonest = (a: MyTaskRow, b: MyTaskRow) => dueAtTime(a.dueAt) - dueAtTime(b.dueAt)
  buckets.overdue.sort(bySoonest)
  buckets.dueToday.sort(bySoonest)
  buckets.dueLater.sort(bySoonest)
  return buckets
}

/**
 * Case-insensitive substring filter over task title + organization name.
 * An empty / whitespace query returns the full list unchanged.
 */
export function filterTasksBySearch(tasks: ReadonlyArray<MyTaskRow>, query: string): MyTaskRow[] {
  const q = query.trim().toLowerCase()
  if (!q) {
    return [...tasks]
  }
  return tasks.filter(
    (task) => task.title.toLowerCase().includes(q) || task.organization.name.toLowerCase().includes(q),
  )
}

/**
 * Narrow an open-task list to the ones worth a notification today:
 * still actionable (OPEN / IN_PROGRESS) AND due by end of today. Sorted
 * soonest-due first so the most urgent sits at the top of the bell.
 */
export function selectDueTodayTasks(tasks: ReadonlyArray<MyTaskRow>, now: Date = new Date()): MyTaskRow[] {
  return tasks
    .filter((t) => (t.status === 'OPEN' || t.status === 'IN_PROGRESS') && isDueByEndOfToday(t.dueAt, now))
    .sort((a, b) => {
      const at = a.dueAt ? new Date(a.dueAt).getTime() : Number.POSITIVE_INFINITY
      const bt = b.dueAt ? new Date(b.dueAt).getTime() : Number.POSITIVE_INFINITY
      return at - bt
    })
}

/** One group in the top-bar bell's task list. */
export interface BellTaskSection {
  title: string
  overdue: boolean
  tasks: MyTaskRow[]
}

/**
 * The bell's task groups: Overdue first, then Due today. The feed is
 * `selectDueTodayTasks` (nothing due after today), split by the Tasks page's
 * own `bucketTasksByDue`, so the bell and the Tasks page group by one rule.
 * Empty groups are dropped.
 */
export function bellTaskSections(tasks: ReadonlyArray<MyTaskRow>, now: Date = new Date()): BellTaskSection[] {
  const { overdue, dueToday } = bucketTasksByDue(selectDueTodayTasks(tasks, now), now)
  const sections: BellTaskSection[] = [
    { title: 'Overdue', overdue: true, tasks: overdue },
    { title: 'Due today', overdue: false, tasks: dueToday },
  ]
  return sections.filter((section) => section.tasks.length > 0)
}

/**
 * View-state selector for the `/tasks/:id` detail page. Pure so the
 * page stays a thin switch and the branch logic is unit-tested without
 * a DOM. `errorStatus` is the `ApiError.status` (or null when there is
 * no error); a 404 maps to the friendly not-found state and takes
 * precedence over any stale cached task react-query may still hold.
 */
export type TaskDetailViewState =
  | 'loading'
  | 'not_found'
  | 'error'
  | 'workflow_redirect'
  | 'render_completed'
  | 'render_active'

// ---------------------------------------------------------------------------
// Tasks-board rollup (one ticket per day / mealtime / drop)
// ---------------------------------------------------------------------------

/**
 * A synthetic board row that collapses several per-campus shipment tasks
 * into ONE ticket. Shaped as a `MyTaskRow` so the due-bucket / search /
 * TaskList pipeline needs no changes; `rollupTasks` carries the members
 * the completion wizard steps through.
 */
export interface TaskRollupRow extends MyTaskRow {
  rollupTasks: MyTaskRow[]
}

export type TaskBoardRow = MyTaskRow | TaskRollupRow

export function isTaskRollupRow(row: TaskBoardRow): row is TaskRollupRow {
  return Array.isArray((row as TaskRollupRow).rollupTasks)
}

/**
 * Grouping key for the board rollup — delegates to the ONE shared
 * `taskRollupKey` (`apps/ordo/shared/task-rollup-key.ts`) that the server's
 * `GET /api/tasks/counts` also counts by, so the sidebar badge equals the
 * number of tickets this board lists:
 *   - CHEF_SUBMIT_TEMP_LOG (kitchen temp logs) → one per DELIVERY DAY, all
 *     locations inside (its member sheets are the wizard steps).
 *   - SCHOOL_COMPLETE_SERVICE / SCHOOL_CONFIRM_DELIVERY_RECEIVED → one per
 *     (campus, SERVICE day). That is exactly the unit the Production
 *     Records page works in (PROD-1277): its Transport logs wizard covers
 *     every truck feeding the day and its Service records wizard covers
 *     every mealtime, so the collapsed ticket navigates there instead of
 *     stepping through member sheets.
 *
 * Returns null (no grouping) for other subKinds, rows without shipment
 * metadata (e.g. the chef's synthesized day rows), and closed rows.
 */
export function rollupKeyForTask(task: MyTaskRow): string | null {
  return taskRollupKey(task)
}

/** "Mon, Aug 31" for an ISO yyyy-mm-dd calendar day (zone-safe: local parts). */
function formatIsoDay(iso: string): string {
  const parts = calendarDayParts(iso)
  if (!parts) {
    return iso
  }
  return new Date(parts.year, parts.month - 1, parts.day).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

/** Step label inside the rollup wizard: which campus (or mealtime) is next. */
export function rollupStepLabel(task: MyTaskRow): string {
  const s = task.shipment
  if (!s) {
    return task.title
  }
  if (isSchoolProductionTask(task.subKind)) {
    // School groups are single-campus, single-day — the members are its
    // mealtime shipments.
    return formatLabel(s.mealtime)
  }
  if (task.subKind === 'CHEF_SUBMIT_TEMP_LOG') {
    return `${s.schoolLocationName} · ${formatLabel(s.mealtime)}`
  }
  return s.schoolLocationName
}

/**
 * Board title for a rollup group: "Submit temperature logs — Mon, Aug 31 ·
 * 3 campuses" for the kitchen; "Service records · Lincoln Elementary ·
 * Thu, Sep 10" for the school tasks (one campus, one service day).
 */
export function rollupTitle(subKind: string, members: ReadonlyArray<MyTaskRow>): string {
  const first = members[0]!
  const s = first.shipment!
  const campusCount = new Set(members.map((m) => m.shipment?.schoolLocationId)).size
  const campuses = `${campusCount} campus${campusCount === 1 ? '' : 'es'}`
  switch (subKind) {
    case 'CHEF_SUBMIT_TEMP_LOG':
      return `Submit temperature logs — ${formatIsoDay(s.deliveryDate)} · ${campuses}`
    case 'SCHOOL_COMPLETE_SERVICE':
    case 'SCHOOL_CONFIRM_DELIVERY_RECEIVED':
      return `${SCHOOL_TASK_LABELS[subKind]} · ${s.schoolLocationName} · ${formatIsoDay(s.serviceDate)}`
    default:
      return first.title
  }
}

/**
 * Collapse rollup-eligible tasks into one synthetic ticket per group,
 * keeping every other row untouched and preserving list order (a group
 * renders where its first member sat). Groups of ONE pass through as the
 * plain task so a single campus never sees wizard chrome. Pure —
 * unit-tested.
 */
export function buildTaskRollups(tasks: ReadonlyArray<MyTaskRow>): TaskBoardRow[] {
  const groups = new Map<string, MyTaskRow[]>()
  for (const task of tasks) {
    const key = rollupKeyForTask(task)
    if (key) {
      const list = groups.get(key) ?? []
      list.push(task)
      groups.set(key, list)
    }
  }
  const emitted = new Set<string>()
  const out: TaskBoardRow[] = []
  for (const task of tasks) {
    const key = rollupKeyForTask(task)
    if (!key) {
      out.push(task)
      continue
    }
    if (emitted.has(key)) {
      continue
    }
    emitted.add(key)
    const members = groups.get(key)!
    if (members.length === 1) {
      out.push(task)
      continue
    }
    const dueTimes = members.map((m) => dueAtTime(m.dueAt)).filter((t) => Number.isFinite(t))
    const subKind = task.subKind!
    out.push({
      ...task,
      id: `rollup:${key}`,
      title: rollupTitle(subKind, members),
      description: isSchoolProductionTask(subKind)
        ? `${members.length} shipments on this day. Opens the day on Production Records.`
        : `${members.length} tasks rolled into one — Complete steps through each campus in turn.`,
      dueAt: dueTimes.length > 0 ? new Date(Math.min(...dueTimes)).toISOString() : null,
      blocksSubjectId: null,
      commentCount: undefined,
      shipment: null,
      rollupTasks: members,
    })
  }
  return out
}

/**
 * The rollup-wizard step to land on after `justDoneId` finishes: the
 * first not-done task after it (wrapping), or null when everything is
 * done. Pure — unit-tested; consumed by `task-rollup-drawer.tsx`.
 */
export function nextOpenStepId(
  tasks: ReadonlyArray<Pick<MyTaskRow, 'id'>>,
  doneIds: ReadonlySet<string>,
  justDoneId: string,
): string | null {
  const start = tasks.findIndex((t) => t.id === justDoneId)
  for (let offset = 1; offset <= tasks.length; offset += 1) {
    const candidate = tasks[(start + offset) % tasks.length]!
    if (candidate.id !== justDoneId && !doneIds.has(candidate.id)) {
      return candidate.id
    }
  }
  return null
}

export function taskDetailViewState(input: {
  isLoading: boolean
  task: (WorkflowRouteTask & Pick<MyTaskRow, 'status'>) | null | undefined
  errorStatus: number | null
}): TaskDetailViewState {
  if (input.errorStatus !== null) {
    return input.errorStatus === 404 ? 'not_found' : 'error'
  }
  if (input.isLoading) {
    return 'loading'
  }
  if (!input.task) {
    return 'not_found'
  }
  if (workflowRouteForTask(input.task)) {
    return 'workflow_redirect'
  }
  if (input.task.status === 'COMPLETED' || input.task.status === 'DISMISSED') {
    return 'render_completed'
  }
  return 'render_active'
}
